import { useUpdateMediaFolderMutation } from "@api/media-folders";
import Button from "@components/ui/button";
import Spinner from "@components/ui/spinner";
import Switch from "@components/ui/switch";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useState } from "react";
import { toast } from "react-toastify";
import { track } from "@/features/analytics";
import { useGetCurrentUserQuery, useGetUsersQuery } from "@/shared/services";
import {
  type IMediaFolder,
  type IMediaFolderUser,
  MediaFolderPermEnum,
} from "@/shared/types";
import { MediaLibDialogBase } from "../../../media-lib-dialog-base";
import { MediaLibMembersFolderDialogInput } from "./media-lib-folder-members-dialog-input";
import { MediaLibFolderMembersDialog } from "./media-lib-folder-members-dialog-users";

const DIALOG_ID = "media-lib-folder-members";

interface IProps {
  folderData: IMediaFolder;
}

export const MediaLibMembersFolderDialog = ({ folderData }: IProps) => {
  const dispatch = useAppDispatch();
  const [selectedUsers, setSelectedUsers] = useState<IMediaFolderUser[]>([
    ...(folderData.users ?? []),
  ]);

  const { data: currentUser } = useGetCurrentUserQuery();

  const [isShareEveryone, setIsShareEveryone] = useState(folderData.openAccess);
  const { isFetching: isLoadingUsers } = useGetUsersQuery();
  const [updateFolder, { isLoading: isUpdatingFolder }] =
    useUpdateMediaFolderMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (isUpdatingFolder) return;

    try {
      await updateFolder({
        id: folderData._id,
        openAccess: isShareEveryone,
        users: isShareEveryone ? null : selectedUsers,
      }).unwrap();
    } catch (e) {
      track("error updating media folder members", {
        folderId: folderData._id,
        error: e,
      });
      toast.error("Error updating folder members");
    }

    handleClose();
  };

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header
        className="tw-w-[512px]"
        title={`People with access to "${folderData.title}" folder`}
        onClose={handleClose}
      />
      <MediaLibDialogBase.Content>
        <div className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-blue-50 tw-p-4">
          <span className="tw-text-[11px] tw-text-blue-800">
            Share with everyone in your organization
          </span>
          <Switch.Toggle
            checked={isShareEveryone}
            onCheckedChange={(val) => {
              if (val === true) {
                setSelectedUsers([]);
              } else {
                if (currentUser) {
                  setSelectedUsers([
                    {
                      id: currentUser.oktaId,
                      type: MediaFolderPermEnum.Owner,
                    },
                  ]);
                }
              }
              setIsShareEveryone(val);
            }}
          />
        </div>
        <div
          className={cn(
            "tw-mt-4",
            isShareEveryone && "tw-pointer-events-none tw-opacity-50",
          )}
        >
          <span className="tw-mb-4 tw-block tw-font-sans tw-text-[12px] tw-font-medium">
            Who has access
          </span>
          <MediaLibMembersFolderDialogInput
            isShareEveryone={isShareEveryone}
            selectedUsers={selectedUsers}
            onSelect={(user) => {
              setSelectedUsers((prev) => [
                ...prev,
                { id: user.oktaId, type: MediaFolderPermEnum.Editor },
              ]);
            }}
          />
        </div>

        {!isShareEveryone && (
          <MediaLibFolderMembersDialog
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
          />
        )}
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoadingUsers}
          className={cn(isUpdatingFolder && "tw-cursor-not-allowed")}
        >
          {isUpdatingFolder ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibMembersFolderDialog.dialogId = DIALOG_ID;
